/**
 * Offline sync contracts (05-api.md `POST {o}/sync`; 02-architecture.md Offline; 06-flows.md
 * F10; M4 task 6): the batch of commands a till (or the outlet hub) queued while the cloud
 * was unreachable, and the result of each.
 *
 * Every queued command keeps the Idempotency-Key it was given when it was queued; the
 * server stores each command's answer under that key, so a batch sent twice, or half of it
 * sent by the till and half by the hub, applies every command once. The commands run in
 * the order given, each in its own transaction: one command's refusal does not undo the
 * others, but later commands on the same order are `skipped` (never applied on top of a
 * refused earlier one). The server prices everything (CLAUDE.md rule 1); `offlineTotalPaise`
 * on `orders.bill` is the total the till billed, which the server keeps when it prices the
 * order differently, raising `pricing.discrepancy` (02-architecture.md).
 */
import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import { uuidSchema } from './ids.js';
import { nonNegativePaiseSchema, paiseSchema } from './money.js';
import {
  addLinesSchema,
  applyDiscountSchema,
  complimentarySchema,
  coversSchema,
  moveTableSchema,
  openOrderSchema,
  reasonSchema,
  removeDiscountSchema,
  sendToKitchenSchema,
  setChargesSchema,
  updateLineSchema,
  voidLineSchema,
} from './orders.js';
import { recordTenderSchema, settleOrderSchema } from './payments.js';

/** Most commands in one sync call. */
export const SYNC_MAX_COMMANDS = 100;

const orderId = { orderId: uuidSchema };

/** The payload of each command kind a till may queue. */
export const syncPayloadSchemas = {
  'orders.open': openOrderSchema.extend({ id: uuidSchema }),
  'orders.addLines': addLinesSchema.extend(orderId),
  'orders.updateLine': z.object({ ...orderId, lineId: uuidSchema, changes: updateLineSchema }),
  'orders.voidLine': voidLineSchema.extend({ ...orderId, lineId: uuidSchema }),
  'orders.sendToKitchen': sendToKitchenSchema.extend({
    ...orderId,
    /**
     * The KOT number the till printed from its offline range (`POST {o}/kot-ranges`);
     * the kitchen files the KOT under it instead of drawing a new number.
     */
    kotNo: z.int().min(1).optional(),
  }),
  'orders.applyDiscount': z.object({ ...orderId, discount: applyDiscountSchema }),
  'orders.removeDiscount': removeDiscountSchema.extend({ ...orderId, discountId: uuidSchema }),
  'orders.setCharges': setChargesSchema.extend(orderId),
  'orders.markComplimentary': z.object({ ...orderId, comp: complimentarySchema }),
  'orders.setCovers': coversSchema.extend(orderId),
  'orders.moveTable': moveTableSchema.extend(orderId),
  'orders.bill': z.object({
    ...orderId,
    /** The total the till showed and billed (its local preview); kept when the server differs. */
    offlineTotalPaise: nonNegativePaiseSchema.optional(),
  }),
  'orders.cancel': reasonSchema.extend(orderId),
  'payments.recordTender': recordTenderSchema.extend(orderId),
  'payments.settle': settleOrderSchema.extend({
    ...orderId,
    /**
     * The serial the till printed from its invoice block (`POST {o}/invoice-blocks`) when
     * it settled offline; the server issues the invoice under it. Absent: the next number
     * of the series, as online.
     */
    invoiceNo: z.int().min(1).optional(),
    /** When the till settled (offline): the invoice's date and time of issue. */
    settledAt: isoTimestampSchema.optional(),
  }),
} as const;

export const SYNC_KINDS = Object.keys(syncPayloadSchemas) as [
  keyof typeof syncPayloadSchemas,
  ...(keyof typeof syncPayloadSchemas)[],
];
export type SyncKind = keyof typeof syncPayloadSchemas;

export const syncCommandSchema = z.object({
  /** The Idempotency-Key the command was queued with. */
  idempotencyKey: uuidSchema,
  /** A kind of `syncPayloadSchemas` (an unknown kind is refused for that command alone). */
  kind: z.string().min(1).max(60),
  payload: z.record(z.string(), z.unknown()),
  /** When the till made it. */
  clientAt: isoTimestampSchema,
  /**
   * The order version the till last saw from the server: sent with the first queued
   * command on an order it knew before going offline (409 `VERSION_CONFLICT` as a
   * `conflict` result when the order moved on); absent for orders created offline.
   */
  baseVersion: z.int().min(1).nullable().optional(),
});
export type SyncCommand = z.output<typeof syncCommandSchema>;

/** `POST {o}/sync`. */
export const syncRequestSchema = z.object({
  /** The terminal whose queue (or, for the hub, whose command log) this is. */
  terminalId: uuidSchema,
  commands: z.array(syncCommandSchema).min(1).max(SYNC_MAX_COMMANDS),
});

/**
 * - `applied`   done now.
 * - `replayed`  done by an earlier sync of the same key; its stored answer.
 * - `rejected`  refused for good (validation, rules, permission): drop it, tell the user.
 * - `conflict`  the order moved on (`VERSION_CONFLICT`, with `current`) or the offline
 *               action contradicts the server's state: needs a person.
 * - `skipped`   not tried: an earlier command on the same order was refused.
 * - `retry`     could not be decided now (another request holds the key, a transient
 *               error): keep it queued and send it again.
 */
export const SYNC_STATUSES = [
  'applied',
  'replayed',
  'rejected',
  'conflict',
  'skipped',
  'retry',
] as const;

export const syncDiscrepancySchema = z.object({
  orderId: uuidSchema,
  keptTotalPaise: paiseSchema,
  serverTotalPaise: paiseSchema,
  /** Server minus kept: positive when the outlet under-charged. */
  differencePaise: paiseSchema,
});

export const syncResultSchema = z.object({
  idempotencyKey: uuidSchema,
  kind: z.string(),
  status: z.enum(SYNC_STATUSES),
  /** Stable error code (`ORDER_BILLED`, `VERSION_CONFLICT`…) for `rejected`, `conflict`, `skipped`. */
  code: z.string().nullable(),
  detail: z.string().nullable(),
  fields: z.record(z.string(), z.array(z.string())).nullable(),
  orderId: uuidSchema.nullable(),
  /** The order's version after the command. */
  version: z.int().nullable(),
  /** What the online endpoint would have answered (order, tender, settlement…); null otherwise. */
  result: z.record(z.string(), z.unknown()).nullable(),
  /** `conflict`: the order as the server has it now. */
  current: z.record(z.string(), z.unknown()).nullable(),
  /** `orders.bill`: set when the server priced the order differently than the till did. */
  discrepancy: syncDiscrepancySchema.nullable(),
});
export type SyncResult = z.output<typeof syncResultSchema>;

export const syncResponseSchema = z.object({ results: z.array(syncResultSchema) });
